import { SEOHead } from '../components/SEOHead';
import { ScrollReveal, StaggerContainer, StaggerItem } from '../components/ScrollReveal';
import { CTABanner } from '../components/CTABanner';
import { ServiceFAQ } from '../components/ServiceFAQ';
import { Check, Zap } from 'lucide-react';

const whySwitch = [
  { stat: '₹50K–₹5L+', desc: 'Traditional shoots cost this much and take weeks' },
  { stat: 'Trend over', desc: 'By the time content is ready, the trend has passed' },
  { stat: 'Days, not weeks', desc: 'AI UGC produces multiple creatives in days at a fraction of the cost' },
  { stat: '3x ROAS', desc: 'UGC-style content gets 3x better ROAS than polished brand ads on Meta' },
];

const whatWeProduce = [
  'AI-generated product visuals and lifestyle content',
  'Hook-driven short-form video creatives for Instagram Reels and Meta ads',
  'Testimonial-style and review-format AI video ads',
  'Multiple creative variations for A/B testing',
  'Static image ad creatives for feed and story formats — ready to upload and run',
];

const whoFor = [
  'D2C brands running Meta and Instagram ad campaigns',
  'Ecommerce brands who need consistent, fresh creatives without monthly shoot cycles',
  'Marketing teams dealing with creative fatigue and declining ROAS',
];

const faqs = [
  { question: 'What is AI UGC?', answer: 'AI UGC (User-Generated Content) refers to video and image ad creatives produced using artificial intelligence that look and feel like authentic content created by real users. These are designed to perform like organic content on paid channels such as Meta and Instagram.' },
  { question: 'How is AI UGC different from real UGC?', answer: 'AI UGC is fully produced using AI tools — no real creator shoots the content. Real UGC involves actual creators filming with your product. Both look authentic, but AI UGC is faster, cheaper, and infinitely scalable. Real UGC adds the human trust factor. Many brands use a mix of both.' },
  { question: 'How long does it take to produce AI UGC ads?', answer: 'Most AI UGC projects are delivered within 3-7 days depending on the number of creatives and variations needed. Compared to traditional shoots that take 2-4 weeks, this is significantly faster.' },
  { question: 'How much does AI UGC cost compared to a shoot?', answer: 'AI UGC typically costs 60-80% less than a traditional video shoot. A professional brand shoot can cost ₹50,000 to ₹5,00,000+ while AI UGC delivers comparable quality at a fraction of that price with faster turnaround.' },
  { question: 'Will AI UGC work for my product category?', answer: 'AI UGC works exceptionally well for most D2C product categories including beauty, skincare, fashion, food, supplements, home goods, and electronics. During our discovery call, we assess your specific product and recommend the best approach.' },
];

const serviceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "name": "AI UGC Ads",
  "provider": { "@type": "Organization", "name": "Thriveit Media", "url": "https://thriveitmedia.in" },
  "description": "AI-generated UGC ad creatives for D2C and ecommerce brands. High-quality, scroll-stopping content without traditional shoots.",
  "areaServed": "Worldwide"
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(f => ({
    "@type": "Question",
    "name": f.question,
    "acceptedAnswer": { "@type": "Answer", "text": f.answer }
  }))
};

export default function AIUGCAdsPage() {
  return (
    <>
      <SEOHead
        title="AI UGC Ads Agency | AI Generated Ad Creatives | Thriveit Media"
        description="Thriveit Media produces AI UGC ads for D2C and ecommerce brands. High-quality, scroll-stopping ad creatives using AI — no shoot required."
        canonical="https://thriveitmedia.in/services/ai-ugc-ads"
        schema={[serviceSchema, faqSchema]}
      />

      {/* Hero */}
      <section className="py-32 md:py-40 relative">
        <div className="absolute inset-0 grid-pattern" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <p className="text-xs uppercase tracking-[0.2em] font-heading font-bold text-[#FF5C35] mb-6">AI UGC Ads</p>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-none text-[#F5F5F0] max-w-5xl mb-8">
              AI UGC Ads for D2C and Ecommerce Brands
            </h1>
          </ScrollReveal>
        </div>
      </section>

      {/* What Are AI UGC Ads */}
      <section className="py-24 md:py-32 bg-[#111111]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-[#F5F5F0] mb-8">What Are AI UGC Ads?</h2>
            <p className="text-base md:text-lg font-body font-light text-[#888880] leading-relaxed max-w-3xl">
              Your audience does not want ads that look like ads. They want content that feels real — authentic, relatable, and worth watching. AI UGC ads are high-quality video and image creatives produced using artificial intelligence — designed to look and feel like real user-generated content. Instead of booking a studio, hiring a photographer, and waiting three weeks for edited content, AI UGC allows brands to produce multiple high-quality creatives in days — at a fraction of the cost. UGC-style video content has consistently shown 3x better performance on Meta ads compared to polished brand creatives.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Why Switching */}
      <section className="py-24 md:py-32 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-[#F5F5F0] mb-12">Why D2C Brands Are Switching to AI UGC</h2>
          </ScrollReveal>
          <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {whySwitch.map((item) => (
              <StaggerItem key={item.desc}>
                <div className="bg-[#111111] border border-white/5 p-8 group hover:border-[#FF5C35]/30 transition-all duration-500">
                  <Zap className="w-6 h-6 text-[#FF5C35] mb-4" strokeWidth={1.5} />
                  <p className="text-2xl font-heading font-extrabold text-[#F5F5F0] mb-2">{item.stat}</p>
                  <p className="text-sm font-body font-light text-[#888880] leading-relaxed">{item.desc}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* What We Produce */}
      <section className="py-24 md:py-32 bg-[#111111]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-[#F5F5F0] mb-12">What We Produce</h2>
          </ScrollReveal>
          <StaggerContainer className="space-y-4">
            {whatWeProduce.map((item) => (
              <StaggerItem key={item} className="flex items-start gap-4 bg-[#0a0a0a] border border-white/5 p-6">
                <Check className="w-5 h-5 text-[#FF5C35] mt-0.5 flex-shrink-0" />
                <p className="text-base font-body font-light text-[#F5F5F0]">{item}</p>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Portfolio — Our Work */}
      <section data-testid="ai-ugc-portfolio" className="py-24 md:py-32 bg-[#0a0a0a]">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <p className="text-xs uppercase tracking-[0.2em] font-heading font-bold text-[#FF5C35] mb-4">Portfolio</p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-[#F5F5F0] mb-4">Our AI UGC Ads in Action</h2>
            <p className="text-base md:text-lg font-body font-light text-[#888880] mb-16 max-w-2xl">Real AI UGC ad creatives we have produced for brands. Scroll-stopping content built to convert on Meta, Instagram, and beyond.</p>
          </ScrollReveal>
          <StaggerContainer className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {[
              '1ecMlrxmU6EHWq_HmiRT_wrCoQkRj9iqU',
              '1UzYNEgWnfgegV9UcL8OcBAgfeo7apDbC',
              '1-LizFhvPISVhYbf2LZzxyjH5qFSW9LWE',
              '1a0XbTvKOU-yHNA8VYdyf_MbJq9mykLtT',
              '1yt0qLfCwkAZWuJ-XJRLClcEj4i_EfaPK',
              '1Ea8fzdRzw-EW8w2YsrSg6XArY0v1cYYL',
              '1Zf_pwR_SVmwVWt0k5uh0LUFHzqfxUrit',
              '1jSx-bsRrW5MFdq0PX_wDKw8mF2f-61wI',
            ].map((id, i) => (
              <StaggerItem key={id}>
                <div
                  data-testid={`portfolio-video-${i}`}
                  className="relative rounded-2xl overflow-hidden bg-black shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 hover:shadow-[#FF5C35]/10"
                >
                  <div className="relative w-full overflow-hidden" style={{ paddingBottom: '160%' }}>
                    <iframe
                      src={`https://drive.google.com/file/d/${id}/preview`}
                      className="absolute border-0"
                      style={{ top: '-35%', left: '-35%', width: '170%', height: '170%' }}
                      allow="autoplay"
                      allowFullScreen
                      title={`AI UGC Ad Sample ${i + 1}`}
                      loading="lazy"
                    />
                    <div className="absolute top-0 right-0 w-14 h-14 bg-black z-10 rounded-bl-xl" />
                  </div>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-24 md:py-32 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <ScrollReveal>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-[#F5F5F0] mb-12">Who This Is For</h2>
          </ScrollReveal>
          <StaggerContainer className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {whoFor.map((item, i) => (
              <StaggerItem key={i}>
                <div className="bg-[#111111] border border-white/5 p-8 h-full">
                  <span className="text-4xl font-heading font-extrabold text-white/5">{String(i + 1).padStart(2, '0')}</span>
                  <p className="text-base font-body font-light text-[#888880] mt-4 leading-relaxed">{item}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      <ServiceFAQ faqs={faqs} />

      <CTABanner
        heading="Book a Free AI UGC Consultation"
        subtext="See how AI UGC ads can reduce your creative production costs while improving ad performance."
        buttonText="Book a Free Consultation"
      />
    </>
  );
}
